import React from 'react';
import { Button } from '../ui/button';
import { 
  LayoutGrid, 
  Square, 
  List,
  Grid2X2,
  Grid3X3
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

export const VIEW_MODES = {
  GRID_2: 'grid-2',     // 2 colunas (padrão)
  GRID_3: 'grid-3',     // 3 colunas
  GRID_4: 'grid-4',     // 4 colunas
  LIST: 'list',         // Lista
};

const viewModeConfig = {
  [VIEW_MODES.GRID_2]: {
    icon: Grid2X2,
    label: '2 por linha',
    // Mobile: 2 cols, Desktop: 2 cols
    gridClass: 'grid grid-cols-2 auto-rows-fr gap-4',
    showOnMobile: true,
  },
  [VIEW_MODES.GRID_3]: {
    icon: Grid3X3,
    label: '3 por linha',
    // Mobile: 2 cols, Tablet: 2 cols, Desktop: 3 cols
    gridClass: 'grid grid-cols-2 lg:grid-cols-3 auto-rows-fr gap-4',
    showOnMobile: false,
  },
  [VIEW_MODES.GRID_4]: {
    icon: LayoutGrid,
    label: '4 por linha',
    // Mobile: 2 cols, Tablet: 3 cols, Desktop: 4 cols
    gridClass: 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-fr gap-4',
    showOnMobile: false,
  },
  [VIEW_MODES.LIST]: {
    icon: List,
    label: 'Lista',
    gridClass: 'flex flex-col gap-3',
    showOnMobile: true,
  },
};

export function ViewModeToggle({ viewMode, onViewModeChange, className }) {
  return (
    <TooltipProvider>
      <div className={cn("flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg", className)}>
        {Object.entries(viewModeConfig).map(([mode, config]) => {
          const Icon = config.icon;
          const isActive = viewMode === mode;
          
          return (
            <Tooltip key={mode}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onViewModeChange(mode)}
                  className={cn(
                    "h-8 w-8 p-0 rounded-md transition-all",
                    // Hide non-mobile options on mobile
                    !config.showOnMobile && "hidden sm:flex",
                    isActive 
                      ? "bg-white dark:bg-slate-700 shadow-sm text-orange-600 dark:text-orange-400" 
                      : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                  )}
                  data-testid={`view-mode-${mode}`}
                >
                  <Icon className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{config.label}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

export function getGridClassName(viewMode) {
  return viewModeConfig[viewMode]?.gridClass || viewModeConfig[VIEW_MODES.GRID_2].gridClass;
}

// Hook to persist view mode in localStorage
export function useViewMode(storageKey = 'trips-view-mode') {
  const [viewMode, setViewMode] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(storageKey) || VIEW_MODES.GRID_2;
    }
    return VIEW_MODES.GRID_2;
  });

  const handleViewModeChange = React.useCallback((mode) => {
    setViewMode(mode);
    if (typeof window !== 'undefined') {
      localStorage.setItem(storageKey, mode);
    }
  }, [storageKey]);

  return [viewMode, handleViewModeChange];
}

export default ViewModeToggle;
